import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Layers, Loader2, CheckCircle, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { useRoomFavorites } from '../configurator/useRoomFavorites';
import { ROMAN_FOLDS } from '../../lib/romanFold';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/fabrics`;
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-');

/* One cell = one collection × fold: estimate first, confirm, then run + progress */
const RegenCell = ({ collection, fold, rooms, mode, headers }) => {
  const [estimate, setEstimate] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);
  const body = useMemo(() => ({ collection_name: collection, room_types: rooms, mode, fold, review: true }), [collection, rooms, mode, fold]);
  const id = `roman-regen-${slug(collection)}-${fold}`;

  useEffect(() => {
    setEstimate(null); setConfirming(false);
    if (!rooms.length) return undefined;
    const t = setTimeout(() => axios.post(`${API}/colors/regenerate-all/estimate`, body, { headers }).then(({ data }) => setEstimate(data)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [body, rooms, headers]);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const start = async () => {
    setConfirming(false);
    try {
      const { data } = await axios.post(`${API}/colors/regenerate-all`, body, { headers });
      setJob({ status: 'running', total: data.total, completed: 0, failed: 0 });
      toast.success(`${collection} · ${fold}: ${data.total} scenes started`);
      pollRef.current = setInterval(async () => {
        try {
          const { data: j } = await axios.get(`${API}/visualization-jobs/${data.job_id}`, { headers });
          const failed = (j.results || []).filter(r => r.status === 'error').length;
          setJob(p => ({ ...p, status: j.status, completed: j.completed || 0, failed }));
          if (j.status !== 'running') { clearInterval(pollRef.current); toast.success(`${collection} · ${fold} done (${failed} failed)`); }
        } catch { /* keep polling */ }
      }, 5000);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to start'); }
  };

  const running = job?.status === 'running';
  return (
    <div className="rounded-lg border border-neutral-200 p-3 space-y-2" data-testid={id}>
      <p className="text-[11px] text-neutral-600 min-h-[2.2em]" data-testid={`${id}-estimate`}>
        {!rooms.length ? 'Pick rooms' : !estimate ? 'Estimating…' : estimate.scene_count === 0 ? 'Nothing to generate'
          : <><b>{estimate.scene_count}</b> scenes · ≈ <b>${estimate.estimated_cost_usd}</b> · ~{estimate.estimated_minutes} min</>}
      </p>
      {job ? (
        <div className="text-[11px] text-blue-700" data-testid={`${id}-progress`}>
          <div className="flex items-center gap-1">{running ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}{job.completed}/{job.total}{job.failed > 0 && ` · ${job.failed} failed`}</div>
          <div className="w-full bg-blue-100 rounded-full h-1 mt-1"><div className="bg-blue-600 h-1 rounded-full transition-all" style={{ width: `${job.total ? job.completed / job.total * 100 : 0}%` }} /></div>
        </div>
      ) : confirming ? (
        <div className="flex gap-1">
          <Button size="sm" className="h-7 flex-1 text-[11px] bg-amber-600 hover:bg-amber-700" onClick={start} data-testid={`${id}-confirm`}>Confirm ${estimate?.estimated_cost_usd}</Button>
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setConfirming(false)} data-testid={`${id}-cancel`}>Cancel</Button>
        </div>
      ) : (
        <Button size="sm" variant="outline" className="h-7 w-full text-[11px] gap-1" disabled={!estimate?.scene_count || estimate.scene_count > estimate.max_scenes} onClick={() => setConfirming(true)} data-testid={`${id}-btn`}>
          <RefreshCw className="w-3 h-3" /> Regenerate
        </Button>
      )}
    </div>
  );
};

/* Admin: regenerate Roman collections' AI rooms as real roman shades, per fold style */
export const RomanRegenDialog = ({ token }) => {
  const [open, setOpen] = useState(false);
  const [roomTypes, setRoomTypes] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [mode, setMode] = useState('replace');
  const { favorites, loaded } = useRoomFavorites(token);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [collections, setCollections] = useState([]);

  useEffect(() => { axios.get(`${API}/room-types`).then(({ data }) => setRoomTypes(data)).catch(() => {}); }, []);
  useEffect(() => { if (open) axios.get(`${API}/roman-collections`, { headers }).then(({ data }) => setCollections(data)).catch(() => {}); }, [open, headers]);
  useEffect(() => { if (loaded && open) setRooms(favorites.length ? favorites : ['penthouse_living']); }, [loaded, open]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleRoom = (r) => setRooms(s => (s.includes(r) ? s.filter(x => x !== r) : [...s, r]));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2" data-testid="roman-regen-btn"><Layers className="w-4 h-4" /> Roman Rooms</Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Regenerate Roman Rooms</DialogTitle></DialogHeader>
        <p className="text-xs text-neutral-500 -mt-2">Re-renders Roman collections as real roman shades. Results wait in Review Scenes as pending — live scenes are never replaced until you approve. Flat updates the main scenes; Hobbled and Relaxed get their own scenes for shoppers who pick those folds.</p>
        <div>
          <Label className="text-xs">Rooms</Label>
          <div className="mt-1.5 flex flex-wrap gap-1.5 max-h-32 overflow-y-auto" data-testid="roman-regen-rooms">
            {roomTypes.map(r => (
              <button key={r.id} type="button" onClick={() => toggleRoom(r.id)} data-testid={`roman-regen-room-${r.id}`}
                className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${rooms.includes(r.id) ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-200 text-neutral-600 hover:border-neutral-400'}`}>{r.label}</button>
            ))}
          </div>
        </div>
        <div className="flex gap-2" data-testid="roman-regen-mode">
          {[['replace', 'Replace existing'], ['missing', 'Only missing']].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setMode(k)} data-testid={`roman-regen-mode-${k}`}
              className={`flex-1 rounded-md border px-3 py-1.5 text-xs font-medium ${mode === k ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-200 text-neutral-600'}`}>{label}</button>
          ))}
        </div>
        {collections.length === 0 ? <p className="text-sm text-neutral-500" data-testid="roman-regen-empty">No Roman collections found.</p> : (
          <div className="space-y-3">
            {collections.map(({ name, color_count: count }) => (
              <div key={name}>
                <p className="text-sm font-medium text-neutral-900 mb-1.5">{name} <span className="text-neutral-400 font-normal text-xs">({count} colors)</span></p>
                <div className="grid grid-cols-3 gap-2">
                  {ROMAN_FOLDS.map(([fold, label]) => (
                    <div key={fold}>
                      <p className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">{label}</p>
                      <RegenCell collection={name} fold={fold} rooms={rooms} mode={mode} headers={headers} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
